import Action from "./Action";

// TODO: different name from 'history'
// TODO: can we write something like useState()?
export type PendingChange = () => void;

/**
 * A class that keeps track of changes and the impact on the data.
 * The initial provided state MUST not be altered.
 * Upon each action the history returns a new version of the state.
 **/
export default class History<S> {
	private pendingCommand: Action | undefined;

	private actions: Action[] = [];
	private index = 0;

	private state: S;

	/**
	 * Constructor
	 * @param state The initial state.
	 **/
	constructor(state: S) {
		this.state = state;
	}

	/**
	 * @returns True if some actions have been applied, false otherwise.
	 **/
	public hasPrevious(): boolean {
		return this.pendingCommand !== undefined || this.index > 0;
	}

	/**
	 * @returns True if there are actions that have been reverted.
	 */
	public hasNext(): boolean {
		return this.index < this.actions.length;
	}

	/**
	 * Undo the last applied action.
	 * @returns The new state.
	 **/
	public undo(): S {
		if (this.pendingCommand) {
			this.actions.push(this.pendingCommand);
			this.pendingCommand = undefined;
		} else if (this.index > 0) {
			this.state = this.actions[--this.index].revert(this.state);
		}

		return this.state;
	}

	/**
	 * Redo the last reverted action.
	 * @returns The new state.
	 **/
	public redo(): S {
		if (this.index < this.actions.length) {
			this.state = this.actions[this.index++].apply(this.state);
		}

		return this.state;
	}

	/**
	 * Apply a new action.
	 * All actions that have been reverted will be lost.
	 *
	 * @param action The action to be applied.
	 * @returns The new state.
	 */
	public apply(action: Action): S {
		this.actions.splice(this.index, this.actions.length - this.index, action);
		this.pendingCommand = undefined;

		return this.redo();
	}

	/**
	 * Perform the action without actually affecting the internal state.
	 * @param action The action to be applied in a pending manner.
	 * @returns The state as if the action would be applied.
	 **/
	public pending(action: Action): S {
		this.pendingCommand = action;
		this.actions.splice(this.index, this.actions.length - this.index);

		return action.apply(this.state);
	}

	/**
	 * Commit the last pending action.
	 * @returns The new state after applying the last pending action.
	 **/
	public commit(): S {
		if (this.pendingCommand) {
			this.apply(this.pendingCommand);
		}

		return this.state;
	}

	/**
	 * Get the list of currently executed actions.
	 * @returns The list of executed actions.
	 **/
	public getActions(): Action[] {
		return this.actions.slice(0, this.index);
	}
}
